import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Plus,
  Link as LinkIcon,
  MoreVertical,
  Eye,
  EyeOff,
  Pencil,
  Trash2,
  Building2,
  TrendingUp,
  RefreshCw,
} from 'lucide-react'
import { formatCurrency } from '@/lib/utils'
import api from '@/lib/api'
import type { Account } from '@/types'
import { usePlaidLink } from 'react-plaid-link'
import { useViewModeStore } from '@/stores/viewModeStore'
import { toast } from 'sonner'
import { CreateManualAccountDialog } from '@/components/manual/CreateManualAccountDialog'
import { RenameManualAccountDialog } from '@/components/manual/RenameManualAccountDialog'
import { ConfirmDialog } from '@/components/ConfirmDialog'

export function AccountsPage() {
  const navigate = useNavigate()
  const viewMode = useViewModeStore((state) => state.viewMode)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [showHidden, setShowHidden] = useState(false)
  const [linkToken, setLinkToken] = useState<string | null>(null)
  const [linkBanner, setLinkBanner] = useState<{ type: 'success' | 'info' | 'warning'; message: string } | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [renameTarget, setRenameTarget] = useState<Account | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Account | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)

  const fetchLinkToken = async () => {
    try {
      const response = await api.post('/create_link_token')
      setLinkToken(response.data.link_token)
    } catch (error) {
      console.error('Failed to get link token:', error)
    }
  }

  const fetchAccounts = useCallback(async () => {
    console.log('[AccountsPage] fetchAccounts called with viewMode:', viewMode)
    setIsLoading(true)
    try {
      const response = await api.get('/accounts', {
        params: { include_hidden: showHidden, view: viewMode },
      })
      console.log('[AccountsPage] Got', response.data.accounts?.length, 'accounts')
      setAccounts(response.data.accounts)
    } catch (error) {
      console.error('Failed to fetch accounts:', error)
    } finally {
      setIsLoading(false)
    }
  }, [showHidden, viewMode])

  useEffect(() => {
    fetchAccounts()
  }, [fetchAccounts])

  useEffect(() => {
    if (!linkToken) {
      fetchLinkToken()
    }
  }, [linkToken])

  const showLinkBanner = (type: 'success' | 'info' | 'warning', message: string) => {
    setLinkBanner({ type, message })
    setTimeout(() => setLinkBanner(null), 4000)
  }

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: async (publicToken, metadata) => {
      try {
        const response = await api.post('/exchange_public_token', {
          public_token: publicToken,
          institution_id: metadata?.institution?.institution_id,
          institution_name: metadata?.institution?.name,
        })
        const data = response.data ?? {}
        const bank = data.institution_name || metadata?.institution?.name || 'your bank'
        if (data.was_existing) {
          if (data.previous_state === 'error') {
            showLinkBanner('success', `${bank} reconnected — resyncing transactions`)
          } else {
            showLinkBanner('info', `${bank} connection refreshed`)
          }
        } else {
          showLinkBanner('success', `${bank} linked successfully`)
        }
        await fetchAccounts()
      } catch (error: any) {
        // Backend returns 409 if same institution_id but different item_id
        if (error?.response?.status === 409 && error?.response?.data?.error === 'duplicate_institution') {
          const data = error.response.data
          const bank = data.institution_name || 'this institution'
          showLinkBanner('warning', `${bank} is already connected. Confirm in account settings to add as a separate connection.`)
        } else {
          console.error('Failed to link account:', error)
          showLinkBanner('warning', 'Failed to link bank. Please try again.')
        }
      }
    },
  })

  const toggleAccountVisibility = async (account: Account) => {
    try {
      await api.put(`/accounts/${account.id}`, {
        is_hidden: !account.is_hidden,
      })
      await fetchAccounts()
    } catch (error) {
      console.error('Failed to update account:', error)
    }
  }

  const handleDeleteManualAccount = async () => {
    if (deleteTarget?.manual_id == null) {
      return
    }
    setIsDeleting(true)
    try {
      await api.delete(`/manual_accounts/${deleteTarget.manual_id}`)
      toast.success('Account removed')
      setDeleteTarget(null)
      await fetchAccounts()
    } catch (error) {
      console.error('Failed to delete account:', error)
      toast.error('Unable to remove account')
    } finally {
      setIsDeleting(false)
    }
  }

  // Update connection functionality (for Plaid re-authentication)
  const [updateLinkToken, setUpdateLinkToken] = useState<string | null>(null)

  const { open: openUpdateLink, ready: updateLinkReady } = usePlaidLink({
    token: updateLinkToken,
    onSuccess: async () => {
      setUpdateLinkToken(null)
      await api.post('/refresh_accounts')
      await fetchAccounts()
      toast.success('Connection updated')
    },
    onExit: () => {
      setUpdateLinkToken(null)
    },
  })

  // Effect to open update link when token is ready
  useEffect(() => {
    if (updateLinkToken && updateLinkReady) {
      openUpdateLink()
    }
  }, [updateLinkToken, updateLinkReady, openUpdateLink])

  // Launch Plaid update mode for an existing connection. Update mode is the only
  // way to grant access to accounts opened after the original link (e.g. a new
  // card at an already-connected bank); the backend enables account_selection.
  const handleUpdateConnection = async (account: Account) => {
    if (account.plaid_item_id == null) return
    try {
      const response = await api.post('/create_update_link_token', {
        item_id: account.plaid_item_id,
      })
      setUpdateLinkToken(response.data.link_token)
    } catch (error) {
      console.error('Failed to create update link token:', error)
      toast.error('Unable to start the update. Please try again.')
    }
  }

  const bankAccounts = accounts.filter(
    (a) => a.type === 'depository' || a.type === 'credit'
  )
  const investmentAccounts = accounts.filter(
    (a) => a.type === 'investment' || a.type === 'brokerage'
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-32" />
          <Skeleton className="h-10 w-40" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i}>
              <CardContent className="p-6">
                <Skeleton className="h-6 w-32 mb-4" />
                <Skeleton className="h-8 w-24" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    )
  }

  const AccountCard = ({ account }: { account: Account }) => (
    <Card
      className={`group cursor-pointer transition-all hover:shadow-md ${
        account.is_hidden ? 'opacity-60' : ''
      }`}
      onClick={() => navigate(`/accounts/${account.id}`)}
    >
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 dark:bg-primary-900/30">
              {account.type === 'investment' || account.type === 'brokerage' ? (
                <TrendingUp className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              ) : (
                <Building2 className="h-5 w-5 text-primary-600 dark:text-primary-400" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  {account.custom_name || account.name}
                </h3>
                {viewMode === 'household' && account.owner && (
                  <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                    {account.owner.nickname || account.owner.email.split('@')[0]}
                  </span>
                )}
              </div>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {account.institution_name || 'Manual account'}
                {account.mask && ` ••••${account.mask}`}
              </p>
            </div>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button
                variant="ghost"
                size="icon"
                className="opacity-0 group-hover:opacity-100"
              >
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {!account.is_manual && account.plaid_item_id != null && (
                <>
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation()
                      handleUpdateConnection(account)
                    }}
                  >
                    <RefreshCw className="mr-2 h-4 w-4" />
                    Update Connection
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  if (account.is_manual) setRenameTarget(account)
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation()
                  toggleAccountVisibility(account)
                }}
              >
                {account.is_hidden ? (
                  <>
                    <Eye className="mr-2 h-4 w-4" />
                    Show
                  </>
                ) : (
                  <>
                    <EyeOff className="mr-2 h-4 w-4" />
                    Hide
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-danger"
                onClick={(e) => {
                  e.stopPropagation()
                  if (account.is_manual) setDeleteTarget(account)
                }}
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Remove
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-4">
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {formatCurrency(account.balance)}
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Badge variant="secondary" className="capitalize">
              {account.subtype || account.type}
            </Badge>
            {account.is_manual && <Badge variant="outline">Manual</Badge>}
            {account.is_hidden && <Badge variant="outline">Hidden</Badge>}
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      {linkBanner && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm font-medium ${
            linkBanner.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950/40 dark:text-green-300'
              : linkBanner.type === 'warning'
                ? 'border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300'
                : 'border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-300'
          }`}
        >
          {linkBanner.message}
        </div>
      )}
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Accounts
        </h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => setShowHidden(!showHidden)}
          >
            {showHidden ? (
              <>
                <EyeOff className="mr-2 h-4 w-4" />
                Hide Hidden
              </>
            ) : (
              <>
                <Eye className="mr-2 h-4 w-4" />
                Show Hidden
              </>
            )}
          </Button>
          <Button onClick={() => open()} disabled={!ready}>
            <LinkIcon className="mr-2 h-4 w-4" />
            Link Bank
          </Button>
          <Button variant="outline" onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Manual Account
          </Button>
        </div>
      </div>

      {/* Bank Accounts */}
      {bankAccounts.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Bank Accounts
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {bankAccounts.map((account) => (
              <AccountCard key={account.id} account={account} />
            ))}
          </div>
        </div>
      )}

      {/* Investment Accounts */}
      {investmentAccounts.length > 0 && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Investment Accounts
          </h2>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {investmentAccounts.map((account) => (
              <AccountCard key={account.id} account={account} />
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {accounts.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Building2 className="mb-4 h-12 w-12 text-gray-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              No accounts yet
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Link a bank account or add a manual account to get started.
            </p>
            <div className="mt-4 flex gap-2">
              <Button onClick={() => open()} disabled={!ready}>
                <LinkIcon className="mr-2 h-4 w-4" />
                Link Bank Account
              </Button>
              <Button variant="outline" onClick={() => setCreateOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Manual Account
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <CreateManualAccountDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(created) => {
          toast.success('Account created')
          fetchAccounts()
          if (created?.id) {
            navigate(`/accounts/manual_${created.id}`)
          }
        }}
      />

      {renameTarget?.manual_id != null && (
        <RenameManualAccountDialog
          open={renameTarget !== null}
          onOpenChange={(open) => {
            if (!open) setRenameTarget(null)
          }}
          accountId={renameTarget.manual_id}
          currentName={renameTarget.name}
          onRenamed={() => {
            toast.success('Account renamed')
            fetchAccounts()
          }}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null)
        }}
        title="Remove account?"
        description="This permanently deletes the account and all of its holdings."
        confirmLabel="Remove"
        destructive
        isConfirming={isDeleting}
        onConfirm={handleDeleteManualAccount}
      />
    </div>
  )
}
